import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { login, sendForgotOTP, resetPassword, googleAuth } from '../services/api';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { GoogleLogin } from '@react-oauth/google';
import '../styles/global.css';
import '../styles/Auth.css';

type Step = 'login' | 'forgot' | 'otp' | 'reset';

const Login = () => {
  const [step, setStep]         = useState<Step>('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp]           = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // ── LOGIN ──
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!executeRecaptcha) return toast.error('Captcha not ready!');
    setLoading(true);
    try {
      const captchaToken = await executeRecaptcha('login');
      const res = await login({ email, password, captchaToken });
      loginUser(res.data);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // ── SEND FORGOT OTP ──
  const handleSendForgotOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendForgotOTP({ email });
      toast.success('OTP sent to your email!');
      setStep('otp');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error sending OTP');
    } finally {
      setLoading(false);
    }
  };

  // ── VERIFY OTP ──
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error('Enter 6 digit OTP!');
    setStep('reset');
  };

  // ── RESET PASSWORD ──
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error('Password Should be of minimum 6 characters!');
    setLoading(true);
    try {
      await resetPassword({ email, otp, newPassword });
      toast.success('Password Reset Successfull.');
      setStep('login');
      setOtp('');
      setNewPassword('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  // ── GOOGLE LOGIN ──
  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const res = await googleAuth({ credential: credentialResponse.credential });
      loginUser(res.data);
      toast.success('Welcome! 🎉');
      navigate('/dashboard');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Google login failed');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-orb1" />
      <div className="auth-orb2" />

      <div className="auth-wrap">
        <div className="auth-logo">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div className="auth-logo-text">my<em>linx</em></div>
          </Link>
          <p className="auth-logo-sub">
            {step === 'login'  && 'Welcome back'}
            {step === 'forgot' && 'Forgot password'}
            {step === 'otp'    && 'Enter OTP'}
            {step === 'reset'  && 'Set new password'}
          </p>
        </div>

        <div className="auth-card">

          {/* ── LOGIN ── */}
          {step === 'login' && (
            <>
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@gmail.com" required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" required
                    className="form-input"
                  />
                </div>

                <div style={{ textAlign: 'right', marginBottom: 20, marginTop: -8 }}>
                  <button
                    type="button"
                    onClick={() => setStep('forgot')}
                    style={{
                      background: 'none', border: 'none',
                      fontFamily: 'var(--font-mono)', fontSize: 11,
                      color: 'var(--accent)', cursor: 'pointer'
                    }}
                  >
                    Forgot password?
                  </button>
                </div>

                <button type="submit" disabled={loading} className="auth-btn">
                  {loading ? 'Signing in...' : 'Sign In →'}
                </button>
              </form>

              <div className="auth-divider">
                <span>or</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => toast.error('Google login failed')}
                  theme="outline"
                  shape="rectangular"
                  text="signin_with"
                />
              </div>
            </>
          )}

          {/* ── FORGOT ── */}
          {step === 'forgot' && (
            <form onSubmit={handleSendForgotOTP}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔐</div>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7 }}>
                   Enter Your email
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@gmail.com" required
                  className="form-input"
                />
              </div>

              <button type="submit" disabled={loading} className="auth-btn">
                {loading ? 'Sending OTP...' : 'Send OTP →'}
              </button>

              <button type="button" onClick={() => setStep('login')}
                style={{
                  width: '100%', marginTop: 12,
                  background: 'none', border: 'none',
                  fontFamily: 'var(--font-mono)', fontSize: 12,
                  color: 'var(--muted)', cursor: 'pointer'
                }}
              >
                ← Back to login
              </button>
            </form>
          )}

          {/* ── OTP ── */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📧</div>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7 }}>
                  OTP Sent<br />
                  <strong style={{ color: 'var(--ink)' }}>{email}</strong> pe
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Enter OTP</label>
                <input
                  type="text" value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="••••••" maxLength={6} required
                  className="form-input otp-input"
                />
              </div>

              <button type="submit" disabled={loading} className="auth-btn">
                Verify OTP →
              </button>

              <button type="button" onClick={() => setStep('forgot')}
                style={{
                  width: '100%', marginTop: 12,
                  background: 'none', border: 'none',
                  fontFamily: 'var(--font-mono)', fontSize: 12,
                  color: 'var(--muted)', cursor: 'pointer'
                }}
              >
                ← Resend OTP
              </button>
            </form>
          )}

          {/* ── RESET ── */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔑</div>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7 }}>
                  Set New Password
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••" required
                  className="form-input"
                />
              </div>

              <button type="submit" disabled={loading} className="auth-btn">
                {loading ? 'Resetting...' : 'Reset Password →'}
              </button>
            </form>
          )}

          <div className="form-divider" />

          <p className="auth-footer-text">
            Don't have an account?{' '}
            <Link to="/register">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;